export const STOREALIAS_REGEXP = new RegExp(
  '^' +
    '(?:' +
    '(?:' +
    '[a-z0-9\\u00a1-\\uffff]' +
    '[a-z0-9\\u00a1-\\uffff_-]{4,}' +
    '[a-z0-9\\u00a1-\\uffff]' +
    ')' +
    ')' +
    '$',
  'i',
);

export interface ParsedStoreLink {
  storeAlias: string;
  storeUrl: string;
  queryParams: Record<string, string>;
  fragment: string | null;
  locationLabel: string | null;
}

export interface ParseStoreLinkOptions {
  /**
   * When true, accept custom/external domains (e.g. https://myrestaurant.com).
   *
   * This is intended for GO Kiosk only.
   */
  allowExternalDomains?: boolean;
}

/**
 * Parses a store link or alias for the kiosk "enter store URL" flow.
 *
 * Default scope (intentional): ONLY tenant domains (<alias>.<domainUrl>) and bare aliases.
 *
 * GO Kiosk can opt-in to support custom/external domains by passing { allowExternalDomains: true }.
 */
export function parseStoreLink(
  input: string,
  domainUrl: string,
  options: ParseStoreLinkOptions = {},
): ParsedStoreLink | null {
  const value = String(input ?? '').trim();
  if (!value) {
    return null;
  }

  const normalizedDomain = String(domainUrl ?? '')
    .trim()
    .toLowerCase();
  if (!normalizedDomain) {
    return null;
  }

  const allowExternalDomains = options.allowExternalDomains === true;

  // 1) Bare alias only.
  //    Do not treat alias with path/query/hash as valid here; this keeps the UX predictable.
  const hasUrlSeparators = /[\/\?#]/.test(value);
  const hasDot = value.includes('.');
  if (!hasDot && !hasUrlSeparators) {
    if (!STOREALIAS_REGEXP.test(value)) {
      return null;
    }

    const alias = value.toLowerCase();
    return {
      storeAlias: alias,
      storeUrl: `https://${alias}.${normalizedDomain}`,
      queryParams: {},
      fragment: null,
      locationLabel: null,
    };
  }

  // 2) Full URL or host-like value.
  // Normalize to a valid URL for parsing.
  const withProtocol = /^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//.test(value)
    ? value
    : `https://${value}`;

  let url: URL;
  try {
    url = new URL(withProtocol);
  } catch {
    return null;
  }

  const hostnameRaw = String(url.hostname ?? '').trim();
  if (!hostnameRaw) {
    return null;
  }

  const queryParams: Record<string, string> = {};
  url.searchParams.forEach((v, k) => {
    queryParams[k] = v;
  });

  const fragment = url.hash ? url.hash.substring(1) : null;
  const locationLabel = extractLocationLabelFromFragment(fragment);

  const hostname = hostnameRaw.replace(/^www\./i, '').toLowerCase();

  // GO Kiosk: accept external/custom domains and pass the hostname through.
  // The backend can resolve store by external domain OR fall back to first-part alias.
  if (allowExternalDomains) {
    return {
      storeAlias: hostname,
      storeUrl: `https://${hostname}`,
      queryParams,
      fragment,
      locationLabel,
    };
  }

  // Default: enforce tenant domain format: <alias>.<domainUrl>
  const hostnameParts = hostname.split('.').filter(Boolean);
  const domainParts = normalizedDomain.split('.').filter(Boolean);

  if (hostnameParts.length !== domainParts.length + 1) {
    return null;
  }

  const hostnameDomain = hostnameParts.slice(-domainParts.length).join('.');
  if (hostnameDomain !== normalizedDomain) {
    return null;
  }

  const alias = hostnameParts[0];
  if (!STOREALIAS_REGEXP.test(alias)) {
    return null;
  }

  return {
    storeAlias: alias,
    storeUrl: `https://${alias}.${normalizedDomain}`,
    queryParams,
    fragment,
    locationLabel,
  };
}

function extractLocationLabelFromFragment(
  fragment: string | null,
): string | null {
  if (!fragment) {
    return null;
  }

  // Support:
  // - "l/500"
  // - "l/500?x=y"
  // - "/l/500" (defensive)
  const trimmed = fragment.replace(/^\//, '');
  const match = trimmed.match(/^l\/([^/?#]+)(?:\?.*)?$/i);
  if (!match?.[1]) {
    return null;
  }

  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}
